
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import useMediaStore from '../stores/useMediaStore';

const InstructorMediaLibrary = () => {
  const navigate = useNavigate();
  const {
    fetchVideos,
    fetchResources,
    fetchFolders,
    videos,
    resources,
    folders,
    uploadVideo,
    uploadResource,
    deleteVideo,
    deleteResource,
    isLoading,
    error
  } = useMediaStore();

  const [activeTab, setActiveTab] = useState('videos');
  const [selectedFolder, setSelectedFolder] = useState('All');
  const [newFolderName, setNewFolderName] = useState('');
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchFolders();
    if (activeTab === 'videos') {
      fetchVideos();
    } else {
      fetchResources();
    }
  }, [activeTab, fetchVideos, fetchResources, fetchFolders]);

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    await fetchFolders(); // Trigger via store
    setNewFolderName('');
    setIsCreatingFolder(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      if (activeTab === 'videos') {
        await uploadVideo(file, selectedFolder === 'All' ? null : selectedFolder);
        fetchVideos();
      } else {
        await uploadResource(file, selectedFolder === 'All' ? null : selectedFolder);
        fetchResources();
      }
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id, type) => {
    if (window.confirm(`Are you sure you want to delete this ${type}?`)) {
      if (type === 'video') {
        await deleteVideo(id);
        fetchVideos();
      } else {
        await deleteResource(id);
        fetchResources();
      }
    }
  };

  const filteredItems = (activeTab === 'videos' ? videos : resources).filter(item =>
    selectedFolder === 'All' || item.folder === selectedFolder
  );

  return (
    <DashboardLayout userRole="instructor">
      <div className="max-w-7xl mx-auto p-6">
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-white mb-6">Media Library</h1>

        {/* Tabs */}
        <div className="flex space-x-4 mb-6 border-b border-neutral-200 dark:border-neutral-700">
          <button
            onClick={() => setActiveTab('videos')}
            className={`pb-2 px-1 font-medium ${activeTab === 'videos' ? 'text-primary-600 border-b-2 border-primary-500' : 'text-neutral-500 hover:text-neutral-700'}`}
          >
            Videos ({videos.length})
          </button>
          <button
            onClick={() => setActiveTab('resources')}
            className={`pb-2 px-1 font-medium ${activeTab === 'resources' ? 'text-primary-600 border-b-2 border-primary-500' : 'text-neutral-500 hover:text-neutral-700'}`}
          >
            Resources ({resources.length})
          </button>
        </div>

        {/* Upload & Folder Controls */}
        <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Upload {activeTab === 'videos' ? 'Video' : 'Resource'}
              </label>
              <input
                type="file"
                onChange={handleFileUpload}
                accept={activeTab === 'videos' ? 'video/*' : '*'}
                className="text-sm text-neutral-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                disabled={uploading}
              />
              {uploading && <p className="text-sm text-primary-600 mt-1">Uploading...</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Folder</label>
              <select
                value={selectedFolder}
                onChange={(e) => setSelectedFolder(e.target.value)}
                className="px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white text-sm"
              >
                <option value="All">All Folders</option>
                {folders.map(folder => (
                  <option key={folder._id} value={folder._id}>{folder.name}</option>
                ))}
              </select>
            </div>

            <div>
              <button
                onClick={() => setIsCreatingFolder(!isCreatingFolder)}
                className="px-3 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded"
              >
                + New Folder
              </button>
              {isCreatingFolder && (
                <div className="mt-2 flex">
                  <input
                    type="text"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    placeholder="Folder name"
                    className="px-2 py-1 text-sm border border-neutral-300 dark:border-neutral-600 rounded-l bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
                    onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                  />
                  <button
                    onClick={handleCreateFolder}
                    className="px-2 py-1 bg-primary-500 text-white text-sm rounded-r"
                  >
                    Create
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Media Grid */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-lg">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📁</div>
            <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">
              No {activeTab} yet
            </h3>
            <p className="text-neutral-600 dark:text-neutral-400">
              Upload your first {activeTab === 'videos' ? 'video' : 'resource'} to get started.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {filteredItems.map(item => (
              <div key={item._id} className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-3 bg-white dark:bg-neutral-800">
                {activeTab === 'videos' ? (
                  <div className="aspect-video bg-black rounded mb-2 flex items-center justify-center">
                    {item.thumbnailUrl ? (
                      <img src={item.thumbnailUrl} alt={item.title} className="w-full h-full object-cover rounded" />
                    ) : (
                      <span className="text-white text-2xl">🎥</span>
                    )}
                  </div>
                ) : (
                  <div className="aspect-video bg-neutral-100 dark:bg-neutral-700 rounded mb-2 flex items-center justify-center">
                    <span className="text-4xl">📄</span>
                  </div>
                )}
                <h4 className="font-medium text-neutral-900 dark:text-white truncate">{item.title}</h4>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                  {item.folderName || 'No folder'}
                </p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                  {item.duration ? `${item.duration} sec` : item.fileSize}
                </p>
                <button
                  onClick={() => handleDelete(item._id, activeTab.slice(0, -1))}
                  className="mt-2 w-full px-2 py-1 bg-red-500 hover:bg-red-600 text-white text-xs rounded"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default InstructorMediaLibrary;

// import React, { useState, useEffect } from 'react';

// import { useNavigate } from 'react-router-dom';
// import DashboardLayout from '../components/DashboardLayout';
// import useMediaStore from '../stores/useMediaStore'; // Updated store
// import axiosClient from '../services/api';

// const InstructorMediaLibrary = () => {
//     const navigate = useNavigate();
//     const {
//         fetchVideos,
//         fetchResources,
//         fetchFolders,
//         videos,
//         resources,
//         folders,
//         renameFolder,
//         deleteFolder,
//         isLoading,
//         error,
//         uploadVideo,
//         uploadResource,
//         deleteVideo,
//         deleteResource
//     } = useMediaStore();

//     const [selectedFolder, setSelectedFolder] = useState('All');
//     const [newFolderName, setNewFolderName] = useState('');
//     const [activeTab, setActiveTab] = useState('videos'); // 'videos' or 'resources'
//     const [isUploading, setIsUploading] = useState(false);
//     const [uploadProgress, setUploadProgress] = useState(0);
//     const [isCreatingFolder, setIsCreatingFolder] = useState(false);
//     const [folderSearch, setFolderSearch] = useState('');
//     const [isRenaming, setIsRenaming] = useState(null);
//     const [renameValue, setRenameValue] = useState('');

//     useEffect(() => {
//         fetchVideos();
//         fetchResources();
//         fetchFolders();
//     }, []);

//     const handleCreateFolder = async () => {
//         if (!newFolderName.trim()) return;

//         setIsCreatingFolder(true);
//         const folderName = newFolderName.trim();

//         try {
//             const response = await axiosClient.post('/folders', {
//                 folderPath: folderName
//             });

//             if (response.data.success) {
//                 setNewFolderName('');

//                 const currentFolders = [...folders.filter(f => f !== 'All')];
//                 if (!currentFolders.includes(folderName)) {
//                     const updatedFolders = ['All', ...currentFolders, folderName].sort((a, b) =>
//                         a === 'All' ? -1 : b === 'All' ? 1 : a.localeCompare(b)
//                     );
//                     useMediaStore.setState({ folders: updatedFolders });
//                     setSelectedFolder(folderName);
//                 }

//                 setTimeout(() => fetchFolders(), 1000);
//                 alert('Folder created successfully!');
//             } else {
//                 alert(response.data.error || 'Failed to create folder');
//             }
//         } catch (error) {
//             console.error('Error creating folder:', error);
//             alert('An error occurred while creating the folder.');
//         } finally {
//             setIsCreatingFolder(false);
//         }
//     };

//     const handleRenameFolder = (folderPath) => {
//         setIsRenaming(folderPath);
//         setRenameValue(folderPath);
//     };

//     const handleRenameSubmit = async (oldPath) => {
//         if (!renameValue.trim() || renameValue === oldPath) {
//             setIsRenaming(null);
//             return;
//         }

//         const success = await renameFolder(oldPath, renameValue.trim());
//         if (success) {
//             if (selectedFolder === oldPath) {
//                 setSelectedFolder(renameValue.trim());
//             }
//         }
//         setIsRenaming(null);
//     };

//     const handleDeleteFolder = async (folderPath) => {
//         if (!window.confirm(`Are you sure you want to delete the folder "${folderPath}"? This will move all contents to "Uncategorized".`)) {
//             return;
//         }

//         const success = await deleteFolder(folderPath);
//         if (success && selectedFolder === folderPath) {
//             setSelectedFolder('All');
//         }
//     };

//     const handleFileUpload = async (event) => {
//         const file = event.target.files[0];
//         if (!file) return;

//         setIsUploading(true);
//         setUploadProgress(0);

//         try {
//             if (activeTab === 'videos') {
//                 await uploadVideo(file, selectedFolder === 'All' ? 'Uncategorized' : selectedFolder, (progress) => {
//                     setUploadProgress(progress);
//                 });
//             } else {
//                 await uploadResource(file, file.name, '', selectedFolder === 'All' ? 'Uncategorized' : selectedFolder);
//             }

//             setIsUploading(false);
//             setUploadProgress(0);
//             alert('Upload successful!');
//         } catch (error) {
//             console.error('Upload error:', error);
//             setIsUploading(false);
//             setUploadProgress(0);
//             alert('An error occurred during upload.');
//         }
//     };

//     const filteredVideos = selectedFolder === 'All'
//         ? videos
//         : videos.filter(video => video.folder === selectedFolder);

//     const filteredResources = selectedFolder === 'All'
//         ? resources
//         : resources.filter(resource => resource.folder === selectedFolder);

//     const handleDelete = async (type, id) => {
//         if (window.confirm('Are you sure you want to delete this item?')) {
//             if (type === 'video') {
//                 await deleteVideo(id);
//             } else {
//                 await deleteResource(id);
//             }
//         }
//     };

//     return (
//         <DashboardLayout userRole="instructor">
//             <div className="max-w-7xl mx-auto p-6">
//                 <h1 className="text-2xl font-bold text-neutral-900 dark:text-white mb-6">Media Library</h1>

//                 {/* Folder Management */}

//                 {/* Folder Management */}
//                 <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6 mb-6">
//                     <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Manage Folders</h2>

//                     {/* Create Folder */}
//                     <div className="flex space-x-4 mb-6">
//                         <input
//                             type="text"
//                             value={newFolderName}
//                             onChange={(e) => setNewFolderName(e.target.value)}
//                             placeholder="Enter new folder name (use / for nested: e.g., Marketing/2024)"
//                             className="flex-1 px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
//                         />
//                         <button
//                             onClick={handleCreateFolder}
//                             disabled={isLoading}
//                             className="px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-400 text-white text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-800 transition-all duration-200"
//                         >
//                             Create Folder
//                         </button>
//                     </div>

//                     {/* Folder Search */}
//                     <div className="mb-4">
//                         <input
//                             type="text"
//                             placeholder="Search folders..."
//                             value={folderSearch}
//                             onChange={(e) => setFolderSearch(e.target.value)}
//                             className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
//                         />
//                     </div>

//                     {/* Folder Dropdown */}
//                     <div className="mb-6">
//                         <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Select Folder</label>
//                         <select
//                             value={selectedFolder}
//                             onChange={(e) => setSelectedFolder(e.target.value)}
//                             className="block w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
//                         >
//                             {folders.map(folder => (
//                                 <option key={folder} value={folder}>
//                                     {folder === 'All' ? folder : folder.replace(/\//g, ' › ')}
//                                 </option>
//                             ))}
//                         </select>
//                     </div>

//                     {/* Manage Folders List */}
//                     <div className="mt-6 p-4 bg-neutral-50 dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-700">
//                         <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">All Folders</h3>
//                         <div className="max-h-64 overflow-y-auto">
//                             {folders
//                                 .filter(f => f !== 'All')
//                                 .filter(f => f.toLowerCase().includes(folderSearch.toLowerCase()))
//                                 .length === 0 ? (
//                                 <p className="text-sm text-neutral-500 dark:text-neutral-400 py-2">No folders found.</p>
//                             ) : (
//                                 <ul className="space-y-2">
//                                     {folders
//                                         .filter(f => f !== 'All')
//                                         .filter(f => f.toLowerCase().includes(folderSearch.toLowerCase()))
//                                         .map(folder => (
//                                             <li
//                                                 key={folder}
//                                                 className="flex items-center justify-between p-3 bg-white dark:bg-neutral-800 rounded border border-neutral-200 dark:border-neutral-700 hover:shadow-sm transition-shadow"
//                                             >
//                                                 {isRenaming === folder ? (
//                                                     <div className="flex-1 flex items-center space-x-2">
//                                                         <input
//                                                             type="text"
//                                                             value={renameValue}
//                                                             onChange={(e) => setRenameValue(e.target.value)}
//                                                             className="flex-1 px-2 py-1 border border-neutral-300 dark:border-neutral-600 rounded text-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500"
//                                                             autoFocus
//                                                         />
//                                                         <button
//                                                             onClick={() => handleRenameSubmit(folder)}
//                                                             className="px-2 py-1 bg-primary-500 hover:bg-primary-600 text-white text-xs rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
//                                                         >
//                                                             Save
//                                                         </button>
//                                                         <button
//                                                             onClick={() => setIsRenaming(null)}
//                                                             className="px-2 py-1 bg-neutral-300 hover:bg-neutral-400 dark:bg-neutral-600 dark:hover:bg-neutral-500 text-neutral-700 dark:text-neutral-200 text-xs rounded focus:outline-none"
//                                                         >
//                                                             Cancel
//                                                         </button>
//                                                     </div>
//                                                 ) : (
//                                                     <div className="flex-1">
//                                                         <div className="flex items-center flex-wrap">
//                                                             {folder.split('/').map((part, i, arr) => (
//                                                                 <React.Fragment key={i}>
//                                                                     <span className="text-sm font-medium text-neutral-900 dark:text-white">{part}</span>
//                                                                     {i < arr.length - 1 && (
//                                                                         <span className="mx-1 text-neutral-400 dark:text-neutral-500 text-sm">/</span>
//                                                                     )}
//                                                                 </React.Fragment>
//                                                             ))}
//                                                         </div>
//                                                     </div>
//                                                 )}
//                                                 {!isRenaming && (
//                                                     <div className="flex space-x-2">
//                                                         <button
//                                                             onClick={() => handleRenameFolder(folder)}
//                                                             className="p-1 text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
//                                                             title="Rename folder"
//                                                         >
//                                                             <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                                                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
//                                                             </svg>
//                                                         </button>
//                                                         <button
//                                                             onClick={() => handleDeleteFolder(folder)}
//                                                             className="p-1 text-danger-500 hover:text-danger-700 transition-colors"
//                                                             title="Delete folder"
//                                                         >
//                                                             <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                                                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
//                                                             </svg>
//                                                         </button>
//                                                     </div>
//                                                 )}
//                                             </li>
//                                         ))}
//                                 </ul>
//                             )}
//                         </div>
//                     </div>
//                 </div>

//                 {/* Tab Navigation */}
//                 <div className="mb-6 border-b border-neutral-200 dark:border-neutral-700">
//                     <nav className="-mb-px flex space-x-8">
//                         <button
//                             onClick={() => setActiveTab('videos')}
//                             className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'videos'
//                                 ? 'border-primary-500 text-primary-600 dark:text-primary-400'
//                                 : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300 dark:text-neutral-400 dark:hover:text-neutral-300'
//                                 }`}
//                         >
//                             Videos
//                         </button>
//                         <button
//                             onClick={() => setActiveTab('resources')}
//                             className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'resources'
//                                 ? 'border-primary-500 text-primary-600 dark:text-primary-400'
//                                 : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300 dark:text-neutral-400 dark:hover:text-neutral-300'
//                                 }`}
//                         >
//                             Resources
//                         </button>
//                     </nav>
//                 </div>

//                 {/* Upload Area */}
//                 <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6 mb-6">
//                     <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
//                         Upload New {activeTab === 'videos' ? 'Video' : 'Resource'}
//                     </h2>
//                     <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-neutral-300 dark:border-neutral-600 border-dashed rounded-md">
//                         <div className="space-y-1 text-center">
//                             <svg
//                                 className="mx-auto h-12 w-12 text-neutral-400"
//                                 stroke="currentColor"
//                                 fill="none"
//                                 viewBox="0 0 48 48"
//                                 aria-hidden="true"
//                             >
//                                 <path
//                                     d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
//                                     strokeWidth={2}
//                                     strokeLinecap="round"
//                                     strokeLinejoin="round"
//                                 />
//                             </svg>
//                             <div className="flex text-sm text-neutral-600 dark:text-neutral-400">
//                                 <label
//                                     htmlFor="file-upload"
//                                     className="relative cursor-pointer bg-white dark:bg-neutral-800 rounded-md font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
//                                 >
//                                     <span>Upload a file</span>
//                                     <input
//                                         id="file-upload"
//                                         name="file-upload"
//                                         type="file"
//                                         className="sr-only"
//                                         onChange={handleFileUpload}
//                                         accept={activeTab === 'videos' ? "video/*" : ".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.zip"}
//                                     />
//                                 </label>
//                                 <p className="pl-1">or drag and drop</p>
//                             </div>
//                             <p className="text-xs text-neutral-500 dark:text-neutral-400">
//                                 {activeTab === 'videos'
//                                     ? 'MP4, MOV, AVI up to 10GB'
//                                     : 'PDF, DOC, XLS, Images, ZIP up to 100MB'
//                                 }
//                             </p>
//                             {isUploading && (
//                                 <div className="mt-2">
//                                     <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2.5">
//                                         <div
//                                             className="bg-primary-600 h-2.5 rounded-full"
//                                             style={{ width: `${uploadProgress}%` }}
//                                         ></div>
//                                     </div>
//                                     <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">{uploadProgress}%</p>
//                                 </div>
//                             )}
//                         </div>
//                     </div>
//                 </div>

//                 {/* Media Grid */}
//                 <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
//                     <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
//                         Your {activeTab === 'videos' ? 'Videos' : 'Resources'}
//                     </h2>
//                     {isLoading ? (
//                         <div className="flex justify-center py-8">
//                             <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
//                         </div>
//                     ) : (
//                         <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
//                             {activeTab === 'videos' && filteredVideos.length > 0 ? (
//                                 filteredVideos.map(video => (
//                                     <div key={video._id} className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-4 hover:shadow-md transition-shadow">
//                                         <div className="aspect-video bg-neutral-100 dark:bg-neutral-700 rounded-md mb-2 flex items-center justify-center">
//                                             {video.thumbnail ? (
//                                                 <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover rounded-md" />
//                                             ) : (
//                                                 <svg className="h-12 w-12 text-neutral-400" fill="currentColor" viewBox="0 0 24 24">
//                                                     <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" />
//                                                 </svg>
//                                             )}
//                                         </div>
//                                         <h3 className="font-medium text-neutral-900 dark:text-white text-sm truncate">{video.title}</h3>
//                                         <p className="text-xs text-neutral-500 dark:text-neutral-400">{video.folder}</p>
//                                         <p className="text-xs text-neutral-500 dark:text-neutral-400">{video.duration} sec</p>
//                                         <button
//                                             onClick={() => handleDelete('video', video._id)}
//                                             className="mt-2 text-xs text-danger-500 hover:text-danger-700"
//                                         >
//                                             Delete
//                                         </button>
//                                     </div>
//                                 ))
//                             ) : activeTab === 'resources' && filteredResources.length > 0 ? (
//                                 filteredResources.map(resource => (
//                                     <div key={resource._id} className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-4 hover:shadow-md transition-shadow">
//                                         <div className="aspect-video bg-neutral-100 dark:bg-neutral-700 rounded-md mb-2 flex items-center justify-center">
//                                             {resource.fileType.startsWith('image/') ? (
//                                                 <img src={resource.filePath} alt={resource.title} className="w-full h-full object-cover rounded-md" />
//                                             ) : (
//                                                 <div className="text-center">
//                                                     <svg className="h-12 w-12 mx-auto text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                                                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
//                                                     </svg>
//                                                     <p className="text-xs mt-1 text-neutral-500 dark:text-neutral-400">{(resource?.fileExtension || '').toUpperCase()}</p>
//                                                 </div>
//                                             )}
//                                         </div>
//                                         <h3 className="font-medium text-neutral-900 dark:text-white text-sm truncate">{resource.title}</h3>
//                                         <p className="text-xs text-neutral-500 dark:text-neutral-400">{resource.folder}</p>
//                                         <p className="text-xs text-neutral-500 dark:text-neutral-400">{(resource.fileSize / 1024 / 1024).toFixed(2)} MB</p>
//                                         <button
//                                             onClick={() => handleDelete('resource', resource._id)}
//                                             className="mt-2 text-xs text-danger-500 hover:text-danger-700"
//                                         >
//                                             Delete
//                                         </button>
//                                     </div>
//                                 ))
//                             ) : (
//                                 <div className="text-center py-8 col-span-full">
//                                     <p className="text-neutral-500 dark:text-neutral-400">
//                                         No {activeTab} found in this folder.
//                                     </p>
//                                 </div>
//                             )}
//                         </div>
//                     )}
//                 </div>
//             </div>
//         </DashboardLayout>
//     );
// };

// export default InstructorMediaLibrary;